import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ReactNode, useEffect, useState } from "react";
import { Ripples } from "@/components/ui/ripples";
import { supabase } from "@/integrations/supabase/client";

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasTeams, setHasTeams] = useState<boolean | null>(null);
  const [hasPending, setHasPending] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setHasTeams(null);
      setHasPending(false);
      setChecking(false);
      return;
    }

    const storedTeamId =
      localStorage.getItem("lineupsync_active_team") ||
      localStorage.getItem("squadsync_active_team");

    setChecking(true);
    const activeQuery = supabase
      .from("team_memberships")
      .select("id")
      .eq("user_id", user.id)
      .eq("status", "active")
      .limit(1);

    const pendingQuery = supabase
      .from("team_memberships")
      .select("id")
      .eq("user_id", user.id)
      .eq("status", "pending_approval" as any)
      .limit(1);

    const ownerQuery = supabase
      .from("teams")
      .select("id")
      .eq("owner_user_id", user.id)
      .limit(1);

    const storedTeamQuery = storedTeamId
      ? supabase.from("teams").select("id").eq("id", storedTeamId).limit(1)
      : Promise.resolve({ data: [], error: null });

    Promise.all([activeQuery, pendingQuery, ownerQuery, storedTeamQuery])
      .then(([active, pending, ownedTeams, storedTeams]) => {
        if (cancelled) return;
        const hasAnyActive =
          (active.data?.length ?? 0) > 0 ||
          (ownedTeams.data?.length ?? 0) > 0 ||
          (storedTeams.data?.length ?? 0) > 0;
        setHasTeams(hasAnyActive);
        setHasPending((pending.data?.length ?? 0) > 0);
        setChecking(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHasTeams(false);
          setHasPending(false);
          setChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, location.pathname]);

  if (loading || checking || (user && hasTeams === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Ripples />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If user has a pending_approval membership and no active team yet,
  // route them to the profile setup screen.
  if (hasPending && !hasTeams && location.pathname !== "/setup-profile") {
    return <Navigate to="/setup-profile" replace />;
  }

  // Existing logic: if user has no teams at all, send them to /welcome.
  const noTeamAllowed = ["/welcome", "/onboarding", "/invite", "/setup-profile"];
  if (!hasTeams && !hasPending && !noTeamAllowed.some((p) => location.pathname.startsWith(p))) {
    // If a pending invite code is stored, resume the join flow instead of /welcome
    let pendingCode: string | null = null;
    try {
      pendingCode = localStorage.getItem("pending_invite_code");
    } catch {}
    if (pendingCode) {
      return <Navigate to={`/invite?code=${encodeURIComponent(pendingCode)}`} replace />;
    }
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
